import React, { useEffect, useRef, useState } from "react";
import {
  Form,
  Input,
  Label,
  FormFeedback,
  Button,
  Row,
  Col,
  Card,
  CardBody,
  Container,
  Spinner,
  Table,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import PurchaseReturnService from "../../../services/OrderingServices/PurchaseReturn";
import PurchaseService from "../../../services/OrderingServices/PurchaseService";
import SupplierService from "../../../services/OrderingServices/SupplierService";
import ItemService from "../../../services/OrderingServices/ItemService";
import UnitService from "../../../services/OrderingServices/UnitService";

const validationSchema = Yup.object({
  returnDate: Yup.date().required("Return date is required"),
  supplierId: Yup.number().required("Supplier is required").min(1, "Select a supplier"),
  purchaseId: Yup.number().required("Purchase is required").min(1, "Select a purchase"),
  remarks: Yup.string(),
  items: Yup.array()
    .of(
      Yup.object({
        productId: Yup.number().required("Product is required"),
        productName: Yup.string().required("Product name is required"),
        unitId: Yup.number().required("Unit is required"),
        quantity: Yup.number().min(1, "Min 1").required("Required"),
        unitPrice: Yup.number().min(0, "Cannot be negative").required("Required"),
      })
    )
    .min(1, "At least one item is required"),
});

const PurchaseReturnForm = () => {
  const navigate = useNavigate();
  const [supplierList, setSupplierList] = useState([]);
  const [purchaseList, setPurchaseList] = useState([]);
  const [productData, setProductData] = useState([]);
  const [unitList, setUnitList] = useState([]);
  const [loading, setLoading] = useState(false);

  const breadcrumbItems = [
    { title: "Back to List", onClick: () => navigate("/purchase-returns") },
  ];

  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        const res = await SupplierService.getList();
        // SupplierService.getList returns a plain array
        setSupplierList(Array.isArray(res) ? res : res?.items || []);
      } catch (e) {
        console.error(e);
      }
    };
    const fetchPurchases = async () => {
      try {
        const res = await PurchaseService.getList({ perPage: 9999 });
        setPurchaseList(res?.items || []);
      } catch (e) {
        console.error(e);
      }
    };
    const fetchProducts = async () => {
      try {
        const res = await ItemService.itemList({ ShowInOrder: false, fetchHidden: true, pageSize: 9999 });
        // ItemService.itemList may return { items: [] } or a plain array
        setProductData(res?.items || (Array.isArray(res) ? res : []));
      } catch (e) {
        console.error(e);
      }
    };
    const fetchUnits = async () => {
      try {
        const res = await UnitService.getList();
        // UnitService.getList returns a plain array
        setUnitList(Array.isArray(res) ? res : res?.items || []);
      } catch (e) {
        console.error(e);
      }
    };

    fetchSuppliers();
    fetchPurchases();
    fetchProducts();
    fetchUnits();
  }, []);

  const formik = useFormik({
    initialValues: {
      returnDate: new Date().toISOString(),
      supplierId: "",
      purchaseId: "",
      remarks: "",
      items: [
        {
          productId: "",
          productName: "",
          unitId: "",
          quantity: "",
          unitPrice: "",
        },
      ],
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      setLoading(true);
      try {
        const payload = {
          returnDate: new Date(values.returnDate).toISOString(),
          supplierId: parseInt(values.supplierId),
          purchaseId: parseInt(values.purchaseId),
          remarks: values.remarks,
          items: values.items.map((item) => ({
            productId: parseInt(item.productId),
            productName: item.productName,
            unitId: parseInt(item.unitId),
            quantity: parseInt(item.quantity),
            unitPrice: parseFloat(item.unitPrice),
          })),
        };
        await PurchaseReturnService.create(payload);
        toast.success("Purchase return created successfully");
        resetForm();
        navigate("/purchase-returns");
      } catch (error) {
        toast.error(
          error?.response?.data?.message || "Failed to create purchase return"
        );
      } finally {
        setLoading(false);
      }
    },
  });

  const supplierOptions = supplierList.map((s) => ({
    value: s.id,
    label: s.name,
  }));

  const purchaseOptions = purchaseList.map((p) => ({
    value: p.id,
    label: `${p.refrenceNo || p.referenceNo || `PUR-${p.id}`}`,
  }));

  const productOptions = productData.map((p) => ({
    value: p.id,
    label: p.name,
    price: p.price,
  }));

  const unitOptions = unitList.map((u) => ({
    value: u.id,
    label: u.name,
  }));

  const handleAddItem = () => {
    formik.setFieldValue("items", [
      ...formik.values.items,
      { productId: "", productName: "", unitId: "", quantity: "", unitPrice: "" },
    ]);
  };

  const handleRemoveItem = (index) => {
    const newItems = formik.values.items.filter((_, i) => i !== index);
    formik.setFieldValue("items", newItems);
  };

  const handleProductChange = (index, option) => {
    formik.setFieldValue(`items[${index}].productId`, option ? option.value : "");
    formik.setFieldValue(`items[${index}].productName`, option ? option.label : "");
    formik.setFieldValue(`items[${index}].unitPrice`, option ? option.price || "" : "");
  };

  const totalReturnAmount = formik.values.items.reduce((sum, item) => {
    const qty = parseFloat(item.quantity) || 0;
    const price = parseFloat(item.unitPrice) || 0;
    return sum + qty * price;
  }, 0);

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title="Create Purchase Return"
          pageTitle="Purchase Returns"
          breadcrumbItems={breadcrumbItems}
        />

        <Form onSubmit={formik.handleSubmit}>
          {/* Basic Info */}
          <Card className="shadow-sm border-0 mb-3">
            <CardBody>
              <h5 className="fw-bold text-primary mb-4">
                <i className="bx bx-undo me-2" />
                Return Information
              </h5>
              <Row className="g-3">
                <Col md={4}>
                  <Label>
                    Return Date <span className="text-danger">*</span>
                  </Label>
                  <Flatpickr
                    className={`form-control ${
                      formik.touched.returnDate && formik.errors.returnDate
                        ? "is-invalid"
                        : ""
                    }`}
                    value={formik.values.returnDate}
                    onChange={([date]) =>
                      formik.setFieldValue("returnDate", date)
                    }
                    options={{
                      enableTime: true,
                      dateFormat: "Y-m-d H:i",
                      time_24hr: true,
                    }}
                    onBlur={() => formik.setFieldTouched("returnDate", true)}
                    placeholder="Select date & time"
                  />
                  {formik.touched.returnDate && formik.errors.returnDate && (
                    <div className="text-danger small mt-1">
                      {formik.errors.returnDate}
                    </div>
                  )}
                </Col>

                <Col md={4}>
                  <Label>
                    Supplier <span className="text-danger">*</span>
                  </Label>
                  <Select
                    options={supplierOptions}
                    value={
                      supplierOptions.find(
                        (o) => o.value === formik.values.supplierId
                      ) || null
                    }
                    onChange={(option) =>
                      formik.setFieldValue(
                        "supplierId",
                        option ? option.value : ""
                      )
                    }
                    onBlur={() => formik.setFieldTouched("supplierId", true)}
                    placeholder="Select Supplier"
                    isClearable
                  />
                  {formik.touched.supplierId && formik.errors.supplierId && (
                    <div className="text-danger small mt-1">
                      {formik.errors.supplierId}
                    </div>
                  )}
                </Col>

                <Col md={4}>
                  <Label>
                    Purchase Reference <span className="text-danger">*</span>
                  </Label>
                  <Select
                    options={purchaseOptions}
                    value={
                      purchaseOptions.find(
                        (o) => o.value === formik.values.purchaseId
                      ) || null
                    }
                    onChange={(option) =>
                      formik.setFieldValue(
                        "purchaseId",
                        option ? option.value : ""
                      )
                    }
                    onBlur={() => formik.setFieldTouched("purchaseId", true)}
                    placeholder="Select Purchase"
                    isClearable
                  />
                  {formik.touched.purchaseId && formik.errors.purchaseId && (
                    <div className="text-danger small mt-1">
                      {formik.errors.purchaseId}
                    </div>
                  )}
                </Col>

                <Col md={12}>
                  <Label>Remarks</Label>
                  <Input
                    type="textarea"
                    name="remarks"
                    rows={2}
                    value={formik.values.remarks}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Optional remarks..."
                  />
                </Col>
              </Row>
            </CardBody>
          </Card>

          {/* Items */}
          <Card className="shadow-sm border-0 mb-3">
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h5 className="fw-bold text-primary mb-0">
                  <i className="bx bx-list-ul me-2" />
                  Return Items ({formik.values.items.length})
                </h5>
                <Button
                  color="success"
                  size="sm"
                  type="button"
                  onClick={handleAddItem}
                >
                  <i className="bx bx-plus me-1" /> Add Item
                </Button>
              </div>

              {typeof formik.errors.items === "string" && (
                <div className="text-danger small mb-3">
                  {formik.errors.items}
                </div>
              )}

              {formik.values.items.map((item, index) => (
                <Card
                  key={index}
                  className="mb-3 border border-light shadow-sm"
                  style={{ background: "#f8f9fa" }}
                >
                  <CardBody className="py-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="fw-semibold text-muted small">
                        Item #{index + 1}
                      </span>
                      {formik.values.items.length > 1 && (
                        <Button
                          color="danger"
                          size="sm"
                          type="button"
                          outline
                          onClick={() => handleRemoveItem(index)}
                          style={{ padding: "2px 8px" }}
                        >
                          <i className="bx bx-trash" />
                        </Button>
                      )}
                    </div>
                    <Row className="g-2">
                      <Col md={4}>
                        <Label className="small">
                          Product <span className="text-danger">*</span>
                        </Label>
                        <Select
                          options={productOptions}
                          value={
                            productOptions.find(
                              (o) => o.value === item.productId
                            ) || null
                          }
                          onChange={(option) =>
                            handleProductChange(index, option)
                          }
                          placeholder="Select Product"
                          isClearable
                          menuPortalTarget={document.body}
                          styles={{
                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                          }}
                        />
                        {formik.touched.items?.[index]?.productId &&
                          formik.errors.items?.[index]?.productId && (
                            <div className="text-danger small mt-1">
                              {formik.errors.items[index].productId}
                            </div>
                          )}
                      </Col>
                      <Col md={2}>
                        <Label className="small">
                          Unit <span className="text-danger">*</span>
                        </Label>
                        <Select
                          options={unitOptions}
                          value={
                            unitOptions.find((o) => o.value === item.unitId) ||
                            null
                          }
                          onChange={(option) =>
                            formik.setFieldValue(
                              `items[${index}].unitId`,
                              option ? option.value : ""
                            )
                          }
                          placeholder="Unit"
                          isClearable
                          menuPortalTarget={document.body}
                          styles={{
                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                          }}
                        />
                        {formik.touched.items?.[index]?.unitId &&
                          formik.errors.items?.[index]?.unitId && (
                            <div className="text-danger small mt-1">
                              {formik.errors.items[index].unitId}
                            </div>
                          )}
                      </Col>
                      <Col md={2}>
                        <Label className="small">
                          Quantity <span className="text-danger">*</span>
                        </Label>
                        <Input
                          type="number"
                          name={`items[${index}].quantity`}
                          value={item.quantity}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          placeholder="Qty"
                          min={1}
                          invalid={
                            !!(
                              formik.touched.items?.[index]?.quantity &&
                              formik.errors.items?.[index]?.quantity
                            )
                          }
                        />
                        <FormFeedback>
                          {formik.errors.items?.[index]?.quantity}
                        </FormFeedback>
                      </Col>
                      <Col md={2}>
                        <Label className="small">
                          Unit Price <span className="text-danger">*</span>
                        </Label>
                        <Input
                          type="number"
                          name={`items[${index}].unitPrice`}
                          value={item.unitPrice}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          placeholder="Price"
                          min={0}
                          invalid={
                            !!(
                              formik.touched.items?.[index]?.unitPrice &&
                              formik.errors.items?.[index]?.unitPrice
                            )
                          }
                        />
                        <FormFeedback>
                          {formik.errors.items?.[index]?.unitPrice}
                        </FormFeedback>
                      </Col>
                      <Col md={2} className="d-flex align-items-end pb-1">
                        <div className="w-100 text-end">
                          <small className="text-muted">Total</small>
                          <div className="fw-bold text-success">
                            Rs.{" "}
                            {(
                              (parseFloat(item.quantity) || 0) *
                              (parseFloat(item.unitPrice) || 0)
                            ).toLocaleString()}
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              ))}
            </CardBody>
          </Card>

          {/* Summary & Submit */}
          <Card className="shadow-sm border-0">
            <CardBody>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <span className="text-muted me-2">Total Return Amount:</span>
                  <span className="fw-bold text-success fs-5">
                    Rs. {totalReturnAmount.toLocaleString()}
                  </span>
                </div>
                <div className="d-flex gap-2">
                  <Button
                    color="secondary"
                    outline
                    type="button"
                    onClick={() => navigate("/purchase-returns")}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  <Button color="primary" type="submit" disabled={loading}>
                    {loading ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <i className="bx bx-save me-1" />
                        Submit Return
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </CardBody>
          </Card>
        </Form>
      </Container>
    </div>
  );
};

export default PurchaseReturnForm;
